/**
 * Admin-Seite: Firmen-Einstellungen.
 *
 * Aktuell nur die Minijob-Verdienstgrenze (€/Monat). Phase P (Kapitel 20.5,
 * Entscheidung #26): pro Firma editierbar (nur Chef/Owner der aktiven Firma),
 * NICHT mehr app-weit — siehe `frontend/src/data/config.ts` und
 * `Company.minijobLimitEur` im Backend-Schema.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useAppConfig, useUpdateAppConfig } from '../data/config';
import { formatMoney } from '../lib/format';
import { hapticError, hapticSuccess } from '../lib/haptics';
import { ApiError } from '../lib/api';

export default function SettingsAdminPage() {
  const { data: config, isLoading, error: loadError } = useAppConfig();
  const mutation = useUpdateAppConfig();

  // Lokaler Form-State — initialisiert sich aus dem geladenen Config-Wert.
  const [minijobInput, setMinijobInput] = useState<string>('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (config) {
      setMinijobInput(String(config.minijobLimitEur).replace('.', ','));
    }
  }, [config]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFeedback(null);
    const numeric = Number(minijobInput.replace(',', '.'));
    if (Number.isNaN(numeric)) {
      setError('Bitte eine Zahl eingeben (z. B. 556,00).');
      hapticError();
      return;
    }
    if (numeric < 100 || numeric > 5000) {
      setError('Wert muss zwischen 100,00 € und 5.000,00 € liegen.');
      hapticError();
      return;
    }
    try {
      await mutation.mutateAsync({ minijobLimitEur: numeric });
      setFeedback(`Gespeichert. Neue Grenze: ${formatMoney(numeric)}.`);
      hapticSuccess();
    } catch (err) {
      hapticError();
      if (err instanceof ApiError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
      else setError('Speichern fehlgeschlagen');
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-8">
      <h2 className="text-xl font-bold">Firmen-Einstellungen</h2>

      {isLoading && <p className="text-slate-400">Lade…</p>}
      {loadError && (
        <p className="text-red-400 text-sm">
          Fehler beim Laden: {(loadError as Error).message}
        </p>
      )}

      {config && (
        <section className="bg-slate-800 rounded-lg p-4 space-y-3">
          <div>
            <h3 className="text-sm uppercase tracking-wider text-slate-400">
              Minijob-Verdienstgrenze
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Monatliche Brutto-Obergrenze für Mitarbeiter mit Beschäftigungsart
              „Minijob" dieser Firma. Wird in deren Übersicht als Ampel angezeigt.
              Gesetzlicher Wert 2026: 603,00 €. Bei einer Gesetzesänderung hier
              anpassen — gilt firmenweit, andere Firmen sind unabhängig davon.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Grenze (€/Monat)</span>
              <input
                className="input"
                inputMode="decimal"
                value={minijobInput}
                onChange={(e) => setMinijobInput(e.target.value)}
              />
            </label>

            {error && <p className="text-sm text-red-400">{error}</p>}
            {feedback && <p className="text-sm text-emerald-400">{feedback}</p>}

            <div className="flex justify-between items-center gap-3">
              <p className="text-xs text-slate-500">
                Zuletzt geändert: {new Date(config.updatedAt).toLocaleString('de-DE')}
              </p>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="px-4 py-2 text-sm rounded bg-brand hover:bg-brand-dark disabled:opacity-50"
              >
                {mutation.isPending ? 'Speichere…' : 'Speichern'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="bg-slate-800/60 rounded-lg p-4">
        <h3 className="text-sm uppercase tracking-wider text-slate-400 mb-2">
          Hinweise zur Minijob-Grenze
        </h3>
        <ul className="text-xs text-slate-400 list-disc list-inside space-y-1">
          <li>Stand 01.01.2026: 603,00 € (gekoppelt an Mindestlohn × 130 h ÷ 12).</li>
          <li>
            Überschreitung droht in einzelnen Monaten ohne Konsequenzen, bei dauerhafter
            Überschreitung verliert der Job den Minijob-Status.
          </li>
          <li>
            Bei Midijob (Übergangsbereich) gilt eine höhere Grenze — bei Bedarf
            anpassen.
          </li>
        </ul>
      </section>
    </div>
  );
}
